import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../ui/confirm';
import { toast } from '../../lib/toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';

const UserManagement = () => {
  const { getAllUsers, createUser, updateUser, deleteUser, isAdmin, user: currentUser } = useAuth();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'user'
  });
  const [formError, setFormError] = useState('');

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    const result = await getAllUsers();
    if (result.success) {
      setUsers(result.users);
    } else {
      toast.error(result.error || 'Failed to load users');
    }
    setIsLoading(false);
  }, [getAllUsers]);

  useEffect(() => {
    if (isAdmin()) {
      loadUsers();
    }
  }, [isAdmin, loadUsers]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormError('');
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.username || !formData.password) {
      setFormError('Username and password are required');
      return;
    }

    if (formData.password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }

    const result = await createUser(formData.username, formData.password, formData.role);
    if (result.success) {
      // toast.success(`User "${formData.username}" created successfully`);
      setFormData({ username: '', password: '', role: 'user' });
      setShowAddForm(false);
      loadUsers();
    } else {
      setFormError(result.error || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setFormError('');

    const updates = {};
    if (formData.username && formData.username !== editingUser.username) {
      updates.username = formData.username;
    }
    if (formData.password) {
      if (formData.password.length < 6) {
        setFormError('Password must be at least 6 characters');
        return;
      }
      updates.password = formData.password;
    }
    if (formData.role !== editingUser.role) {
      updates.role = formData.role;
    }

    if (Object.keys(updates).length === 0) {
      setFormError('No changes to save');
      return;
    }

    const result = await updateUser(editingUser.id, updates);
    if (result.success) {
      // toast.success('User updated successfully');
      setEditingUser(null);
      setFormData({ username: '', password: '', role: 'user' });
      loadUsers();
    } else {
      setFormError(result.error || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    const ok = await confirm({
      title: 'Delete User',
      description: `Delete user "${username}"? This cannot be undone.`,
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) {
      return;
    }

    const result = await deleteUser(userId);
    if (result.success) {
      // toast.success('User deleted successfully');
      loadUsers();
    } else {
      toast.error(result.error || 'Failed to delete user');
    }
  };

  const startEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '',
      role: user.role
    });
    setShowAddForm(false);
    setFormError('');
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setFormData({ username: '', password: '', role: 'user' });
    setFormError('');
  };

  const cancelAdd = () => {
    setShowAddForm(false);
    setFormData({ username: '', password: '', role: 'user' });
    setFormError('');
  };

  if (!isAdmin()) {
    return null;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">User Management</CardTitle>
            {!showAddForm && !editingUser && (
              <Button size="sm" onClick={() => setShowAddForm(true)}>
                + Add User
              </Button>
            )}
          </div>
        </CardHeader>

        {(showAddForm || editingUser) && (
          <CardContent className="space-y-4">
            <h5 className="text-sm font-medium">
              {editingUser ? 'Edit User' : 'Add New User'}
            </h5>

            <form onSubmit={editingUser ? handleUpdateUser : handleAddUser} className="space-y-4">
              {formError && (
                <Alert variant="destructive">
                  <AlertDescription>{formError}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required={!editingUser}
                  autoComplete="off"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  {editingUser ? 'New Password (leave blank to keep current)' : 'Password'}
                </Label>
                <Input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                  minLength="6"
                  autoComplete="new-password"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  disabled={editingUser?.id === 'admin'}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                >
                  <option value="user">User (Control Only)</option>
                  <option value="admin">Admin (Full Access)</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  Users can only control devices. Admins can add/delete devices and manage users.
                </p>
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  {editingUser ? 'Save Changes' : 'Create User'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={editingUser ? cancelEdit : cancelAdd}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Users</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No users found</div>
          ) : (
            <div className="space-y-4">
              {users.map(user => (
                <div
                  key={user.id}
                  className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-lg border border-border/60 bg-card"
                >
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{user.username}</span>
                      <span
                        className={`text-xs font-bold px-2 py-1 rounded-full uppercase ${
                          user.role === 'admin'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {user.role}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Last login: {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startEdit(user)}
                    >
                      Edit
                    </Button>
                    {user.id !== 'admin' && user.id !== currentUser?.id && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteUser(user.id, user.username)}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserManagement;
